import {Component, Input, OnInit, ViewEncapsulation} from '@angular/core';
import {
    ReferenceBookResponse,
    ReferenceBookType,
    TrendApiService,
    TrendFeedbacksResponse,
    TrendResponse,
} from '@npck/af-frontend-api';
import {LocalizationService, rxSpinner} from '@npck/libs/commons';
import {combineLatest} from 'rxjs';
import {mapTrendStatusToStyle} from '../../../../../components/status/status.component';
import {TrendDetailsFilterHolderService} from '../../../../../domain/trend-details-filter-holder.service';
import {FilterField} from '../../../../../components/full-table/filter/filter.component';
import {initialTableEvent, mergeTableEvent, TableEvent} from '../../../../../components/full-table/full-table.component';
import {
    Column,
    ColumnType,
    ELLIPSIS_MEDIUM_TEXT_MAX_LENGTH,
    Row, StatusColumn,
} from '../../../../../components/full-table/table/table.component';
import {DictionaryHolderService, findValueInDictionary} from '../../../../../domain/dictionary-holder.service';

const TREND_FEEDBACKS_TAB = 'tabTrendFeedbacks';

@Component({
    selector: 'af-trend-feedback-tab',
    standalone: false,
    templateUrl: './trend-feedbacks-tab.component.html',
    styleUrl: './trend-feedbacks-tab.component.scss',
    encapsulation: ViewEncapsulation.None
})
export class TrendFeedbacksTabComponent implements OnInit {
    @Input() trend: TrendResponse;
    filterFields: FilterField[] = [
        {
            type: 'text',
            name: 'query',
        },
    ];
    dataSource: Row[] = [];
    tableEvent: TableEvent;
    totalElements: number;
    response: TrendFeedbacksResponse;

    constructor(
        private trendApiService: TrendApiService,
        private dictionaryHolderService: DictionaryHolderService,
        private filterHolderService: TrendDetailsFilterHolderService,
        private localizationService: LocalizationService,
    ) {
    }

    ngOnInit(): void {
        this.loadFeedbacks(
            this.filterHolderService.getState(this.trend.id, TREND_FEEDBACKS_TAB)?.tableEvent
                ?? this.buildInitialTableEvent(),
        );
    }

    loadFeedbacks(tableEvent: TableEvent): void {
        const prevEvent = this.filterHolderService.getState(this.trend.id, TREND_FEEDBACKS_TAB)?.tableEvent;
        this.tableEvent = mergeTableEvent(prevEvent, tableEvent);
        this.filterHolderService.storeTableState(this.trend.id, TREND_FEEDBACKS_TAB, this.tableEvent);
        if (this.tableEvent.spinnerId !== 'paging') {
            this.dataSource = [];
            this.totalElements = 0;
        }
        combineLatest([
            this.dictionaryHolderService.getReference(ReferenceBookType.TREND_STATUS),
            this.dictionaryHolderService.getReference(ReferenceBookType.TREND_RELEVANCE),
            this.dictionaryHolderService.getReference(ReferenceBookType.TREND_CONFIDENCE),
            this.dictionaryHolderService.getReference(ReferenceBookType.TREND_RATE),
            this.dictionaryHolderService.getReference(ReferenceBookType.TREND_REASON),
            this.dictionaryHolderService.getReference(ReferenceBookType.BIN),
            this.trendApiService.getTrendFeedbacks(this.trend.id, {
                search: this.tableEvent.filter.query,
                limit: this.tableEvent.page.pageSize,
                offset: this.tableEvent.page.pageOffset,
                sortBy: this.tableEvent.sortBy?.direction,
                sortOrder: this.tableEvent.sortBy?.direction,
            }),
        ])
            .pipe(rxSpinner(this.tableEvent.spinnerId))
            .subscribe(([statuses, relevances, confidences, rates, reasons, bins, response]) => {
                this.totalElements = response.total;
                this.buildDataSource(response, statuses, relevances, confidences, rates, reasons, bins);
            });
    }

    private buildInitialTableEvent(): TableEvent {
        return {
            ...initialTableEvent,
            spinnerId: 'trendFeedbacks',
            filter: {
                query: '',
            },
            sortBy: {
                columnName: 'createdDate',
                direction: 'desc',
            },
        };
    }

    private buildDataSource(
        response: TrendFeedbacksResponse,
        statuses: ReferenceBookResponse[],
        relevances: ReferenceBookResponse[],
        confidences: ReferenceBookResponse[],
        rates: ReferenceBookResponse[],
        reasons: ReferenceBookResponse[],
        bins: ReferenceBookResponse[],
    ): void {
        this.dataSource = response.items.map((item, index) => {
            const feedbackRate = findValueInDictionary(rates, item.rate, 'name_ru');
            const feedbackReason = findValueInDictionary(reasons, item.reason, 'name_ru');
            const createdByOrganization = findValueInDictionary(bins, item.createdByOrganization, 'name_ru');
            const row: Row = {
                feedbackCreatedDate: <Column>{
                    type: ColumnType.DATE_TIME_SECONDS,
                    sortable: true,
                    text: item.createdDate,
                },
                feedbackUpdatedDate: <Column>{
                    type: ColumnType.DATE_TIME_SECONDS,
                    sortable: true,
                    text: item.updatedDate,
                },
                trendFeedbackStatus: <StatusColumn>{
                    type: ColumnType.STATUS,
                    text: item.isDeleted
                        ? this.localizationService.getLocalizedKey('i18n_af_statusDeleted')
                        : (findValueInDictionary(statuses, item.status, 'name_ru') ?? '-'),
                    style: mapTrendStatusToStyle(item.status, item.isDeleted),
                },
                relevance: <Column>{
                    type: ColumnType.PRIORITY,
                    text: 'LOW',
                },
                confidence: <Column>{
                    type: ColumnType.PRIORITY,
                    text: item.confidence,
                },
                trendRate: <Column>{
                    type: ColumnType.TEXT,
                    text: feedbackRate || '-',
                },
                reason: <Column>{
                    type: ColumnType.TEXT,
                    text: feedbackReason || '-',
                },
                comment: <Column>{
                    type: ColumnType.TEXT,
                    text: item.comment || '-',
                    cellStyle: item.comment?.length > ELLIPSIS_MEDIUM_TEXT_MAX_LENGTH && {
                        ellipsisTextMedium: true,
                    },
                    tooltip: item.comment?.length > ELLIPSIS_MEDIUM_TEXT_MAX_LENGTH && {
                        text: item.comment || '-',
                    },
                },
                feedbackCreatedBy: <Column>{
                    type: ColumnType.TEXT,
                    text: item.createdBy,
                },
                organization: <Column>{
                    type: ColumnType.TEXT,
                    text: createdByOrganization || '-',
                    cellStyle: createdByOrganization?.length > ELLIPSIS_MEDIUM_TEXT_MAX_LENGTH && {
                        ellipsisTextMedium: true,
                    },
                    tooltip: createdByOrganization?.length > ELLIPSIS_MEDIUM_TEXT_MAX_LENGTH && {
                        text: createdByOrganization || '-',
                    },
                },
                id: <Column>{
                    type: ColumnType.TEXT,
                    text: item.id,
                    cellStyle: {
                        ellipsisTextLarge: true,
                    },
                },
            };
            return row;
        });
    }
}
